import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Shield, Users, MessageSquare, Settings, Crown, Trash2, Award,
  GripVertical, LayoutDashboard, Archive, FileText, CreditCard,
  Image as ImageIcon, ChevronRight, Palette, AppWindow, UserCog,
  Layers, Rss, Menu, Globe, BarChart2, Bell, Lock, Database,
  Cpu, Activity, TrendingUp, Clock, Eye, CheckCircle, AlertTriangle,
  Zap, Terminal, Radio, Package, Server, Swords, Gift, Search,
  Compass, ExternalLink, ChevronDown, Check, Sparkles, Filter, SlidersHorizontal, RefreshCw,
  Folder, Gamepad2, FileCode, MapPin, Crosshair, Volume2, ArrowLeft, ArrowDownToLine, Share2
} from "lucide-react";
import { formatApiError } from "../lib/api";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import IDGPagesTab from "./admin/IDGPagesTab";
import IDGGroupsTab from "./admin/IDGGroupsTab";
import IDGAppsTab from "./admin/IDGAppsTab";
import IDGAppearanceTab from "./admin/IDGAppearanceTab";
import IDGSettingsTab from "./admin/IDGSettingsTab";
import IDGMembersTab from "./admin/IDGMembersTab";
import IDGMenusTab from "./admin/IDGMenusTab";
import IDGWidgetsTab from "./admin/IDGWidgetsTab";
import IDGBlocksTab from "./admin/IDGBlocksTab";
import IDGMarketplaceTab from "./admin/IDGMarketplaceTab";
import IDGForumSettingsTab from "./admin/IDGForumSettingsTab";
import IDGChatboxTab from "./admin/IDGChatboxTab";
import IDGLogsTab from "./admin/IDGLogsTab";
import IDGSavedActionsTab from "./admin/IDGSavedActionsTab";
import IDGRssTab from "./admin/IDGRssTab";
import IDGBoardsTab from "./admin/IDGBoardsTab";
import IDGThemeSettingsTab from "./admin/IDGThemeSettingsTab";
import IDGExternalWidgetsTab from "./admin/IDGExternalWidgetsTab";
import IDGLayoutBuilderTab from "./admin/IDGLayoutBuilderTab";
import IDGMenuBuilderTab from "./admin/IDGMenuBuilderTab";
import IDGServersTab from "./admin/IDGServersTab";
import IDGArenaTab from "./admin/IDGArenaTab";
import IDGModerationTab from "./admin/IDGModerationTab";
import AdminWallet from "../components/wallet/AdminWallet";

// ─── Clean Categorized Structure matching INDUNGI Design ─────────────
const NAV_SECTIONS = [
  {
    section: "PANOU PRINCIPAL",
    items: [
      { id: "dashboard", label: "Dashboard & Statistici", icon: LayoutDashboard },
    ]
  },
  {
    section: "ECONOMIE & MONETIZARE",
    items: [
      { id: "wallet", label: "💰 Portofel, Plăți & VIP", icon: CreditCard, badge: "NOU" },
      { id: "marketplace", label: "Marketplace & Shop VIP", icon: Package },
      { id: "cases", label: "Case Opening & Skinuri", icon: Gift },
    ]
  },
  {
    section: "CONȚINUT & STRUCTURĂ",
    items: [
      { id: "boards", label: "Categorii & Forumuri", icon: MessageSquare },
      { id: "servers", label: "Panou Servere Live CS", icon: Server },
      { id: "pages", label: "Pagini Dinamice & HTML", icon: FileText },
      { id: "rss", label: "Auto RSS Importer", icon: Rss },
      { id: "slides", label: "Hero Banner Slideshow", icon: ImageIcon },
    ]
  },
  {
    section: "COMPETITIV & CS2",
    items: [
      { id: "arena", label: "Arena & Matchmaking 5v5", icon: Swords, badge: "CS2" },
      { id: "cases", label: "Case Opening & Skinuri", icon: Gift },
    ]
  },
  {
    section: "COMUNITATE & MEMBRI",
    items: [
      { id: "members", label: "Gestiune Membri", icon: Users },
      { id: "groups", label: "Grupuri & Permisiuni", icon: Shield },
      { id: "queue", label: "Coadă de Moderare", icon: Clock, badgeKey: "queue" },
      { id: "reports", label: "Raportări & Sesizări", icon: AlertTriangle, badgeKey: "reports" },
      { id: "moderation", label: "Moderare Topicuri", icon: Lock },
      { id: "saved_actions", label: "Macro-uri & Răspunsuri", icon: Zap },
      { id: "chatbox", label: "Chatbox & Camere", icon: Radio },
    ]
  },
  {
    section: "DESIGN & PERSONALIZARE",
    items: [
      { id: "appearance", label: "Temă & Culori Master", icon: Palette },
      { id: "theme_settings", label: "IDG Theme Engine", icon: Cpu },
      { id: "menus", label: "Navigare & Meniuri", icon: Menu },
      { id: "widgets", label: "Widget-uri Sidebar", icon: Layers },
      { id: "idg_widgets", label: "Widget-uri Externe", icon: Globe },
      { id: "apps", label: "Module & Extensii", icon: AppWindow },
    ]
  },
  {
    section: "SISTEM & SETĂRI",
    items: [
      { id: "settings", label: "Setări Generale Site", icon: Settings },
      { id: "forum_settings", label: "Parametri Forum", icon: MessageSquare },
      { id: "extended_settings", label: "Setări Avansate Kernel", icon: Terminal },
      { id: "wallet", label: "💰 Plăți, Donații VIP & Portofel", icon: CreditCard },
      { id: "automation", label: "Automatizări & Cron", icon: Activity },
      { id: "logs", label: "Jurnal & Loguri Sistem", icon: Database },
    ]
  }
];

const TAB_LABELS = NAV_SECTIONS.flatMap(s => s.items).reduce((acc, t) => {
  acc[t.id] = t.label;
  return acc;
}, {});

const TAB_ICONS = NAV_SECTIONS.flatMap(s => s.items).reduce((acc, t) => {
  acc[t.id] = t.icon;
  return acc;
}, {});

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Top level hook calls
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return NAV_SECTIONS;
    const q = searchQuery.toLowerCase();
    return NAV_SECTIONS.map(s => ({
      ...s,
      items: s.items.filter(item => 
        item.label.toLowerCase().includes(q) || 
        item.id.toLowerCase().includes(q)
      )
    })).filter(s => s.items.length > 0);
  }, [searchQuery]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[85vh] bg-[#07090e]">
      <div className="text-center space-y-4">
        <div className="w-12 h-12 border-2 border-[var(--tf-primary)] border-t-transparent rounded-none animate-spin mx-auto" />
        <p className="text-zinc-400 text-xs font-mono tracking-widest uppercase font-bold">Se inițializează panoul de control...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" />;
  if (!["admin", "root"].includes(user.role)) return (
    <div className="flex items-center justify-center min-h-[85vh] bg-[#07090e]">
      <div className="border border-red-500/30 bg-[#0b0e15] p-10 text-center space-y-4 shadow-2xl max-w-md">
        <div className="w-16 h-16 bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto">
          <Shield className="w-8 h-8 text-red-400" />
        </div>
        <h2 className="text-red-400 font-black uppercase tracking-widest text-base font-chakra">Acces Restricționat</h2>
        <p className="text-zinc-400 text-xs font-mono leading-relaxed">Doar administratorii autorizați pot accesa această secțiune.</p>
        <Link to="/" className="inline-block tf-btn-secondary px-6 py-2 text-xs font-bold uppercase">
          <span>Înapoi la Forum</span>
        </Link>
      </div>
    </div>
  );

  const TabIcon = TAB_ICONS[tab] || LayoutDashboard;
  const tabLabel = TAB_LABELS[tab] || tab;

  return (
    <div className="min-h-screen bg-[#07090e] text-zinc-100 selection:bg-[var(--tf-primary)] selection:text-black">
      
      {/* ── TOP COMMAND BAR (Matches Downloads.js Top Navigation) ──────────── */}
      <header className="border-b border-white/10 bg-[#0c0f17]/95 backdrop-blur-md sticky top-0 z-40 py-3.5 px-4 md:px-8">
        <div className="flex items-center justify-between gap-4">
          
          {/* Brand Insignia */}
          <div className="flex items-center gap-3">
            <div 
              className="px-3 py-1 text-xs font-black uppercase tracking-wider text-black font-chakra flex items-center gap-2"
              style={{ backgroundColor: "var(--tf-primary)" }}
            >
              <Shield className="w-4 h-4" />
              <span>INDUNGI ACP</span>
            </div>

            <div className="hidden sm:flex items-center gap-2 text-xs text-zinc-400 font-mono">
              <span>ADMIN</span>
              <ChevronRight className="w-3.5 h-3.5 text-zinc-600" />
              <span className="text-white font-bold uppercase">{tabLabel}</span>
            </div>
          </div>

          {/* Right Status & Direct Action */}
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-3 text-xs font-mono text-zinc-400">
              <span className="flex items-center gap-1.5 text-emerald-400 font-bold">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                Cluster Live
              </span>
              <span className="text-zinc-600">•</span>
              <span>Utilizator: <strong className="text-white">{user.username}</strong> ({user.role?.toUpperCase()})</span>
            </div>

            <Link 
              to="/" 
              target="_blank"
              className="tf-btn-primary px-4 py-2 text-xs font-black uppercase flex items-center gap-2"
            >
              <span>
                <Eye className="w-3.5 h-3.5 mr-1" />
                Vezi Forum Live
              </span>
            </Link>
          </div>

        </div>
      </header>

      <div className="flex min-h-[calc(100vh-65px)]">

        {/* ── SIDEBAR (Matching Downloads Left Category Style) ─────────────── */}
        <aside className={`${sidebarCollapsed ? "w-[68px]" : "w-[260px]"} transition-all duration-200 shrink-0 border-r border-white/10 bg-[#090c12]`}>
          <div className="sticky top-[65px] h-[calc(100vh-65px)] flex flex-col justify-between">
            
            {/* Quick Search Filter */}
            <div className="p-3 border-b border-white/10">
              {!sidebarCollapsed ? (
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Caută modul..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 bg-black/60 border border-white/15 text-xs font-mono text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-[var(--tf-primary)]"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white text-xs">
                      ×
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex justify-center py-1">
                  <Search className="w-4 h-4 text-zinc-500" />
                </div>
              )}
            </div>

            {/* Navigation Sections */}
            <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4 scrollbar-thin scrollbar-thumb-white/10" data-testid="admin-sidebar">
              {filteredSections.map((section) => (
                <div key={section.section} className="space-y-1">
                  {!sidebarCollapsed && (
                    <div className="px-3 py-1 text-[9px] font-mono font-bold tracking-[0.2em] text-zinc-500 uppercase">
                      {section.section}
                    </div>
                  )}
                  <div className="space-y-0.5">
                    {section.items.map((t) => {
                      const isActive = tab === t.id;
                      const Icon = t.icon;
                      return (
                        <button
                          key={t.id}
                          onClick={() => setTab(t.id)}
                          title={sidebarCollapsed ? t.label : undefined}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 text-xs font-chakra font-bold transition-all text-left ${
                            isActive
                              ? "bg-[var(--tf-primary)] text-black font-black shadow-md"
                              : "text-zinc-400 hover:text-white hover:bg-white/5"
                          } ${sidebarCollapsed ? "justify-center px-0" : ""}`}
                          data-testid={`admin-tab-${t.id}`}
                        >
                          <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-black" : "text-zinc-400"}`} />
                          
                          {!sidebarCollapsed && (
                            <span className="flex-1 truncate tracking-wide uppercase text-[11px]">
                              {t.label}
                            </span>
                          )}

                          {!sidebarCollapsed && t.badge && (
                            <span className={`text-[8px] font-mono font-black px-1.5 py-0.5 border shrink-0 ${
                              isActive ? "bg-black/20 text-black border-black/40" : "bg-white/10 text-zinc-200 border-white/20"
                            }`}>
                              {t.badge}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>

            {/* Bottom Collapse Toggle */}
            <div className="p-3 border-t border-white/10 bg-black/40">
              <button
                onClick={() => setSidebarCollapsed(v => !v)}
                className="w-full tf-btn-dark py-2 text-xs font-mono flex items-center justify-center gap-2"
              >
                <SlidersHorizontal className="w-3.5 h-3.5" />
                {!sidebarCollapsed && <span>Restrânge Meniul</span>}
              </button>
            </div>

          </div>
        </aside>

        {/* ── MAIN WORKSPACE ──────────────────────────────────────────────── */}
        <main className="flex-1 min-w-0 p-6 md:p-8 space-y-6 max-w-7xl">
          {tab === "dashboard" && <CleanIndungiDashboardTab user={user} setTab={setTab} />}
          {tab === "servers" && <IDGServersTab />}
          {tab === "theme_settings" && <IDGThemeSettingsTab />}
          {tab === "idg_widgets" && <IDGExternalWidgetsTab />}
          {tab === "menus" && <IDGMenusTab />}
          {tab === "widgets" && <IDGWidgetsTab />}
          {tab === "arena" && <IDGArenaTab />}
          {tab === "cases" && <IDGArenaTab />}
          {tab === "members" && <IDGMembersTab />}
          {tab === "groups" && <IDGGroupsTab />}
          {tab === "pages" && <IDGPagesTab />}
          {tab === "marketplace" && <IDGMarketplaceTab />}
          {tab === "users" && <UsersTab />}
          {tab === "boards" && <IDGBoardsTab />}
          {tab === "moderation" && <IDGModerationTab />}
          {tab === "queue" && <IDGModerationTab />}
          {tab === "reports" && <IDGModerationTab />}
          {tab === "apps" && <IDGAppsTab />}
          {tab === "appearance" && <IDGAppearanceTab />}
          {tab === "forum_settings" && <IDGForumSettingsTab />}
          {tab === "chatbox" && <IDGChatboxTab />}
          {tab === "logs" && <IDGLogsTab />}
          {tab === "saved_actions" && <IDGSavedActionsTab />}
          {tab === "rss" && <IDGRssTab />}
          {tab === "settings" && <SettingsTab section="site" />}
          {tab === "extended_settings" && <IDGSettingsTab />}
          {(tab === "payments" || tab === "wallet") && <AdminWallet />}
          {tab === "slides" && <SettingsTab section="slides" />}
          {tab === "automation" && <SettingsTab section="automation" />}
        </main>

      </div>
    </div>
  );
}

// ─── Authentic INDUNGI Dashboard (Matches Downloads.js & Forums) ─────
function CleanIndungiDashboardTab({ user, setTab }) {
  const [stats, setStats] = useState({});
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/stats"),
      api.get("/admin/users"),
    ]).then(([s, u]) => {
      setStats(s.data || {});
      setRecentUsers(u.data?.slice(0, 6) || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const statItems = [
    { label: "Membri Înregistrați", value: stats.users || 0, icon: Users, change: "+14.2% lună" },
    { label: "Subiecte & Topicuri", value: stats.topics || 0, icon: MessageSquare, change: "+8% activitate" },
    { label: "Răspunsuri & Postări", value: stats.posts || 0, icon: FileText, change: "+24% discuții" },
    { label: "Membri VIP Activi", value: stats.vip_count || 0, icon: Crown, change: "Conturi Active" },
    { label: "Utilizatori Online", value: stats.online_now || 0, icon: Activity, change: "Live pe site" },
  ];

  if (loading) return (
    <div className="py-20 text-center space-y-3">
      <div className="w-10 h-10 border-2 border-[var(--tf-primary)] border-t-transparent rounded-none animate-spin mx-auto" />
      <p className="text-zinc-400 text-xs font-mono">Se încarcă telemetria platformei...</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-dashboard">

      {/* Hero Banner (Same as Downloads Hero Header) */}
      <div className="relative border border-white/10 bg-gradient-to-r from-[#0e121c] via-[#090c12] to-[#07090e] p-8 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <div className="flex items-center gap-2">
              <span 
                className="px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-black font-chakra"
                style={{ backgroundColor: "var(--tf-primary)" }}
              >
                ACP CONSOLE v2.6
              </span>
              <span className="px-2.5 py-0.5 text-[10px] font-mono font-bold bg-white/10 text-zinc-200 border border-white/15">
                STATUS: SISTEM OPERAȚIONAL
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl font-black text-white uppercase tracking-tight font-chakra">
              Bine ai revenit, {user?.username}!
            </h1>

            <p className="text-xs text-zinc-400 font-mono leading-relaxed">
              Panoul centralizat INDUNGI pentru administrarea comunității, serverelor live de CS, cozilor din Arena 5v5 și conținutului platformei.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setTab("wallet")}
              className="tf-btn-primary px-5 py-3 text-xs font-black uppercase flex items-center gap-2 shadow-[0_0_15px_rgba(255,79,0,0.3)]"
            >
              <CreditCard className="w-4 h-4" />
              <span>💰 Portofel & Plăți</span>
            </button>
            <button
              onClick={() => setTab("arena")}
              className="tf-btn-secondary px-5 py-3 text-xs font-black uppercase flex items-center gap-2"
            >
              <span>
                <Swords className="w-4 h-4 mr-1 inline" />
                Arena Matchmaking
              </span>
            </button>
            <button
              onClick={() => setTab("boards")}
              className="tf-btn-secondary px-5 py-3 text-xs font-bold uppercase"
            >
              <span>Forumuri & Categorii</span>
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards (Sharp Angular styling matching Downloads) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {statItems.map((st, i) => (
          <div key={i} className="bg-[#0b0e15] border border-white/10 p-5 space-y-3 flex flex-col justify-between">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 block font-bold">{st.label}</span>
                <span className="text-2xl font-black text-white font-mono mt-1 block">{st.value}</span>
              </div>
              <div className="p-2.5 bg-white/5 border border-white/10 text-zinc-300">
                <st.icon className="w-4 h-4" />
              </div>
            </div>

            <div className="pt-2 border-t border-white/5 text-[10px] font-mono text-emerald-400 font-bold flex items-center justify-between">
              <span>{st.change}</span>
              <span className="text-zinc-600">• Live</span>
            </div>
          </div>
        ))}
      </div>

      {/* Activity Chart & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Telemetry Chart */}
        <div className="lg:col-span-2 bg-[#0b0e15] border border-white/10 p-6 space-y-4">
          <div className="flex items-center justify-between border-b border-white/10 pb-4">
            <div className="flex items-center gap-2 font-chakra font-black uppercase text-sm text-white">
              <TrendingUp className="w-4 h-4 text-[var(--tf-primary)]" />
              <span>Telemetrie Activitate Săptămânală</span>
            </div>
            <div className="flex gap-4 text-xs font-mono">
              <span className="flex items-center gap-1.5 text-zinc-300">
                <span className="w-2.5 h-1 bg-[var(--tf-primary)]" /> Postări
              </span>
              <span className="flex items-center gap-1.5 text-zinc-300">
                <span className="w-2.5 h-1 bg-white/40" /> Înregistrări
              </span>
            </div>
          </div>

          <div className="relative h-48 w-full pt-2">
            <svg className="w-full h-full" viewBox="0 0 600 150" preserveAspectRatio="none">
              <defs>
                <linearGradient id="gPostari" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--tf-primary)" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="var(--tf-primary)" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="gMembri" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity="0.15" />
                  <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
                </linearGradient>
              </defs>
              {[30, 75, 120].map(y => <line key={y} x1="0" y1={y} x2="600" y2={y} stroke="white" strokeOpacity="0.05" strokeDasharray="4" />)}
              <path d="M0,140 C100,120 200,110 300,90 C400,70 500,130 600,100 L600,150 L0,150 Z" fill="url(#gMembri)" />
              <path d="M0,140 C100,120 200,110 300,90 C400,70 500,130 600,100" fill="none" stroke="#ffffff" strokeOpacity="0.4" strokeWidth="1.5" />
              <path d="M0,120 C100,80 200,50 300,75 C400,110 500,35 600,40 L600,150 L0,150 Z" fill="url(#gPostari)" />
              <path d="M0,120 C100,80 200,50 300,75 C400,110 500,35 600,40" fill="none" stroke="var(--tf-primary)" strokeWidth="2.5" />
            </svg>
          </div>

          <div className="flex justify-between text-xs font-mono text-zinc-500 px-2 pt-2 border-t border-white/5">
            {["Luni", "Marți", "Miercuri", "Joi", "Vineri", "Sâmbătă", "Duminică"].map(d => <span key={d}>{d}</span>)}
          </div>
        </div>

        {/* Quick Launch Shortcuts */}
        <div className="space-y-6">
          <div className="bg-[#0b0e15] border border-white/10 p-6 space-y-4">
            <div className="flex items-center gap-2 border-b border-white/10 pb-3 font-chakra font-black uppercase text-xs text-white">
              <Zap className="w-4 h-4 text-[var(--tf-primary)]" />
              <span>Acțiuni Rapide Administrare</span>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "Meciuri Arena", icon: Swords, tabId: "arena" },
                { label: "Gestiune Membri", icon: Users, tabId: "members" },
                { label: "Grupuri & Grade", icon: Shield, tabId: "groups" },
                { label: "Servere Live CS", icon: Server, tabId: "servers" },
                { label: "Stil & Culori", icon: Palette, tabId: "appearance" },
                { label: "Jurnal Loguri", icon: Database, tabId: "logs" },
              ].map(a => (
                <button
                  key={a.tabId}
                  onClick={() => setTab(a.tabId)}
                  className="p-3 bg-black/50 hover:bg-white/5 border border-white/10 hover:border-white/20 transition-all text-left group"
                >
                  <a.icon className="w-4 h-4 text-zinc-400 group-hover:text-[var(--tf-primary)] mb-2 transition-colors" />
                  <span className="text-xs font-chakra font-bold text-zinc-300 group-hover:text-white block truncate uppercase">{a.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Recent Users List */}
          <div className="bg-[#0b0e15] border border-white/10 p-6 space-y-3">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div className="flex items-center gap-2 font-chakra font-black uppercase text-xs text-white">
                <Users className="w-4 h-4 text-[var(--tf-primary)]" />
                <span>Membri Noi</span>
              </div>
              <span className="text-[10px] font-mono text-zinc-500">Ultimii 6</span>
            </div>

            <div className="space-y-2">
              {recentUsers.map((u) => (
                <div key={u.id} className="flex items-center justify-between py-1 border-b border-white/5 last:border-0 font-mono text-xs">
                  <div className="flex items-center gap-2 truncate">
                    <div className="w-6 h-6 bg-white/5 border border-white/10 flex items-center justify-center font-bold text-[10px] text-zinc-200">
                      {u.username?.substring(0, 2).toUpperCase()}
                    </div>
                    <span className="text-zinc-200 truncate">{u.username}</span>
                  </div>
                  <span className="text-[10px] text-zinc-500 shrink-0">{new Date(u.joined_at).toLocaleDateString("ro-RO")}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

      {/* System Health Indicators */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "API Backend", detail: "FastAPI • Port 8000", status: "online", icon: Cpu },
          { label: "Bază de Date", detail: "MongoDB v7 • Connected", status: "online", icon: Database },
          { label: "Matchmaker Daemon", detail: "CS2 Arena Engine Live", status: "online", icon: Swords },
          { label: "RSS Feed Importer", detail: "Sincronizare Automată", status: "online", icon: Rss },
        ].map(s => (
          <div key={s.label} className="p-4 bg-[#0b0e15] border border-white/10 flex items-center gap-3">
            <div className="w-9 h-9 bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0">
              <s.icon className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="text-xs font-bold text-white font-chakra uppercase truncate">{s.label}</div>
              <div className="text-[10px] font-mono text-zinc-400 mt-0.5 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span>{s.detail}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
}

// ─── Users Tab ─────────────────────────────────────────────────────
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/users").then(r => { setUsers(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleRole = async (uid, role) => {
    try {
      await api.put(`/admin/users/${uid}/role`, { role });
      toast.success("Rol actualizat cu succes!");
      const r = await api.get("/admin/users");
      setUsers(r.data);
    } catch(e) {
      toast.error(formatApiError(e));
    }
  };

  const handleVip = async (uid) => {
    try {
      await api.post(`/admin/users/${uid}/vip`, { days: 30 });
      toast.success("VIP acordat pentru 30 zile!");
      const r = await api.get("/admin/users");
      setUsers(r.data);
    } catch(e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = users.filter(u => u.username.toLowerCase().includes(q.toLowerCase()) || u.email?.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <input 
          placeholder="Caută după username sau email..." 
          value={q} 
          onChange={e => setQ(e.target.value)} 
          className="w-80 px-4 py-2 bg-black/60 border border-white/15 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)]" 
        />
        <span className="text-xs font-mono text-zinc-400">Total utilizatori: <strong className="text-white">{filtered.length}</strong></span>
      </div>

      <div className="border border-white/10 bg-[#0b0e15] overflow-hidden">
        <table className="w-full text-left text-xs font-mono">
          <thead>
            <tr className="border-b border-white/10 text-zinc-400 uppercase text-[10px] bg-black/40">
              <th className="p-4 font-chakra font-bold">Utilizator</th>
              <th className="p-4 font-chakra font-bold">Email</th>
              <th className="p-4 font-chakra font-bold">Rol</th>
              <th className="p-4 font-chakra font-bold">VIP</th>
              <th className="p-4 font-chakra font-bold">Puncte</th>
              <th className="p-4 text-right font-chakra font-bold">Acțiuni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {filtered.map(u => (
              <tr key={u.id} className="hover:bg-white/[0.02]">
                <td className="p-4 font-bold text-white flex items-center gap-2">
                  <div className="w-7 h-7 bg-white/5 border border-white/10 flex items-center justify-center font-bold text-xs">
                    {u.username?.[0]?.toUpperCase()}
                  </div>
                  {u.username}
                </td>
                <td className="p-4 text-zinc-400">{u.email}</td>
                <td className="p-4">
                  <select 
                    value={u.role} 
                    onChange={e => handleRole(u.id, e.target.value)}
                    className="bg-black/80 border border-white/15 px-2 py-1 text-xs text-white font-mono"
                  >
                    <option value="user">User</option>
                    <option value="mod">Mod</option>
                    <option value="admin">Admin</option>
                    <option value="root">Root</option>
                  </select>
                </td>
                <td className="p-4">
                  {u.is_vip ? <span className="text-amber-400 font-bold">★ VIP Activ</span> : <span className="text-zinc-600">-</span>}
                </td>
                <td className="p-4 text-zinc-300 font-bold">{u.points || 0}</td>
                <td className="p-4 text-right">
                  <button onClick={() => handleVip(u.id)} className="px-3 py-1 bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] font-bold uppercase hover:bg-amber-500/30 transition-all font-chakra">
                    +30 Zile VIP
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Placeholders ───────────────────────────────────────────────────
function ModerationTab() {
  return (
    <div className="p-8 bg-[#0b0e15] border border-white/10 text-center space-y-3">
      <Lock className="w-12 h-12 text-[var(--tf-primary)] mx-auto opacity-40" />
      <h3 className="text-base font-black uppercase text-white font-chakra">Consola de Moderare Topicuri</h3>
      <p className="text-xs text-zinc-400 max-w-md mx-auto font-mono">Gestionează topicurile închise, mutate, pinned sau șterse direct din panoul de control.</p>
    </div>
  );
}

function ModQueueTab() {
  return (
    <div className="p-8 bg-[#0b0e15] border border-white/10 text-center space-y-3">
      <Clock className="w-12 h-12 text-amber-400 mx-auto opacity-40" />
      <h3 className="text-base font-black uppercase text-white font-chakra">Coadă Aprobare Postări</h3>
      <p className="text-xs text-zinc-400 max-w-md mx-auto font-mono">Toate postările și fișierele ce necesită verificare manuală înainte de publicare apar aici.</p>
    </div>
  );
}

function ReportsTab() {
  return (
    <div className="p-8 bg-[#0b0e15] border border-white/10 text-center space-y-3">
      <AlertTriangle className="w-12 h-12 text-red-400 mx-auto opacity-40" />
      <h3 className="text-base font-black uppercase text-white font-chakra">Raportări & Tichete Membri</h3>
      <p className="text-xs text-zinc-400 max-w-md mx-auto font-mono">Tichetele de abuz, limbaj nepotrivit sau spam transmise de comunitate.</p>
    </div>
  );
}

function SettingsTab({ section }) {
  return <IDGSettingsTab defaultSection={section} />;
}
